import React, { useCallback, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { differenceBy, flatMap, intersectionBy, uniqBy } from 'lodash'
import {
  SubTitleSelectionStyle,
  TitleSelectionStyle,
} from '../analytics-details-v2/view/readership-profile/ReadershipProfileStyles'
import InputSelectionButton from '../analytics-details-v2/view/readership-profile/inputs/InputSelectionButton'
import {
  ReadershipProfileInputTypeInterface,
  StudyObjectGroupCompareEnum,
} from '../analytics-details-v2/view/readership-profile/configs/StudiesObjectsCompareGroupTypesConfig'
import InputTypeSwitchInput from '../analytics-details-v2/view/readership-profile/inputs/InputTypeSwitchInput'
import { ReactComponent as RevertIcon } from '../../assets/icons/app/revert-icon.svg'
import { BookInterface } from '../../core/interface/book.interface'
import { PrivateFiltersItemsAnalyticsConfig } from '../../core/config/analytics-dashboard/private-filters-items-analytics.config'
import { TrendsItemFilterActionInterface } from '../../core/interface/dynamics/trends-item-filter-action.interface'
import DynamicsResultsFilterDropDown from '../dynamics-results/filters/actions-filters/DynamicsResultsFilterDropDown'
import DynamicsResultsFilterType from '../dynamics-results/filters/actions-filters/DynamicsResultsFilterType'
import { DateFilterTypeEnum } from '../../core/enums/date-filter-type.enum'
import { FILTER_KEY_PUBLISHING_DATE } from '../../core/config/dynamics/filters-items-dynamics.config'
import DynamicsResultsFilterActionTag from '../dynamics-results/filters/actions-filters/DynamicsResultsFilterActionTag'
import filterBooksUtil from '../../core/utils/filter-books.util'
import NewReadershipProfileBookFiltersInterface from '../../core/interface/analytics/new-readership-profile-book-filters'

const RevertButtonStyle = styled.button`
  color: #3b3b3b;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Fira Sans, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(37, 37, 37, 0.1);
  height: 28px;
`
interface AllInputsInterface {
  id: string
  books: BookInterface[]
}

type GroupedBooks = {
  publishing_date: Set<number>
  edition: Set<string>
  format: Set<string>
}

const allInputsToAllUniqBooks = (
  allInputs: AllInputsInterface[],
): BookInterface[] => uniqBy(flatMap(allInputs, 'books'), 'id_ean')

const InputTypeChoice = <InputType, ValueType>({
  disable,
  keyValue,
  color,
  title,
  subTitle,
  inputsTypeConfig,
  inputTypeSelected,
  inputValue,
  handleOnClickInputSelectionButton,
  handleSelectValue,
  booksSelected,
  handleUpdateBooksSelected,
  handleOpenBookDetails,
  eanSelected,
  filtersActions,
  handleOnChangeFilter,
  isPrivate = false,
}: {
  disable: boolean
  keyValue: string
  color: string
  title: string
  subTitle: string
  inputsTypeConfig: ReadershipProfileInputTypeInterface<InputType>[]
  inputTypeSelected: InputType
  handleOnClickInputSelectionButton: (type: InputType) => void
  inputValue: ValueType
  handleSelectValue: (type: ValueType) => void
  booksSelected: BookInterface[]
  handleUpdateBooksSelected: (books: BookInterface[]) => void
  handleOpenBookDetails: (eanId: number | null) => void
  eanSelected: number | null
  filtersActions?: NewReadershipProfileBookFiltersInterface
  handleOnChangeFilter?: (
    key: string | number,
    newList: Array<string | number | null> | DateFilterTypeEnum,
  ) => void
  isPrivate?: boolean
}) => {
  const { t } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null)
  const [allInputs, setAllInputs] = useState<Array<AllInputsInterface>>([])

  const addInput = useCallback(
    (id: string, books: BookInterface[]) => {
      setAllInputs((prev) => {
        if (prev.find((input) => input.id === id)) {
          return prev
        }

        const newUniqBook = differenceBy(books, booksSelected, 'id_ean')
        const newBooksFiltered = filterBooksUtil(newUniqBook, filtersActions)
        handleUpdateBooksSelected([...booksSelected, ...newBooksFiltered])
        return [...prev, { id, books }]
      })
    },
    [filtersActions, handleUpdateBooksSelected],
  )

  const removeInput = useCallback(
    (id: string) => {
      const newAllEditions = allInputs.filter((edition) => edition.id !== id)
      setAllInputs(newAllEditions)
      const uniqBooks: BookInterface[] = allInputsToAllUniqBooks(newAllEditions)

      handleUpdateBooksSelected(
        intersectionBy(booksSelected, uniqBooks, 'id_ean'),
      )
    },
    [booksSelected, allInputs, handleUpdateBooksSelected],
  )

  const onChangeFilters = useCallback(
    (
      key: string | number,
      newList: Array<string | number | null> | DateFilterTypeEnum,
    ) => {
      if (handleOnChangeFilter && filtersActions) {
        handleOnChangeFilter(key, newList)
        const filteredBooks = filterBooksUtil(
          allInputsToAllUniqBooks(allInputs),
          {
            ...filtersActions,
            [key as
              | 'authors'
              | 'edition'
              | 'format'
              | 'publishing_date'
              | 'publishing_data_type']: newList,
          },
        )
        handleUpdateBooksSelected(filteredBooks)
      }
    },
    [
      allInputs,
      filtersActions,
      handleOnChangeFilter,
      handleUpdateBooksSelected,
    ],
  )

  const handleYearTypeSelection = (value: DateFilterTypeEnum | null) => {
    onChangeFilters(
      `${FILTER_KEY_PUBLISHING_DATE}_type`,
      value ?? DateFilterTypeEnum.BETWEEN,
    )
  }

  const onClickReinitButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    handleSelectValue(null as ValueType)
    setAllInputs([])
    handleUpdateBooksSelected([])
  }

  const groupedBooks = useMemo<GroupedBooks>(() => {
    const grouped = allInputsToAllUniqBooks(allInputs).reduce(
      (acc, cur) => {
        if (cur.publishing_date) {
          const year = new Date(cur.publishing_date).getFullYear()
          acc.publishing_date.add(year)
        }
        if (cur.edition) acc.edition.add(cur.edition)
        if (cur.format) acc.format.add(cur.format)
        return acc
      },
      {
        publishing_date: new Set<number>(),
        edition: new Set<string>(),
        format: new Set<string>(),
      },
    )

    const sortedYears = Array.from(grouped.publishing_date).sort(
      (a, b) => a - b,
    )

    return {
      ...grouped,
      publishing_date: new Set(sortedYears),
    }
  }, [allInputs])

  const handleRemoveItemFilter = (
    columnKey: string,
    value: string | number,
  ) => {
    if (!filtersActions) return
    onChangeFilters(
      columnKey,
      columnKey === FILTER_KEY_PUBLISHING_DATE
        ? [null, null]
        : filtersActions[columnKey as 'authors' | 'edition' | 'format'].filter(
            (val: string | number) => val !== value,
          ),
    )
  }

  return (
    <>
      <div className="flex-none flex flex-col">
        <TitleSelectionStyle>{title}</TitleSelectionStyle>
        <div className="flex flex-row items-center justify-between">
          <SubTitleSelectionStyle className="h-full flex items-end">
            <span>{subTitle}</span>
          </SubTitleSelectionStyle>
          {inputValue &&
            inputTypeSelected !== StudyObjectGroupCompareEnum.USERS_LIST && (
              <RevertButtonStyle
                disabled={disable}
                type="button"
                className="flex flex-row items-center justify-center gap-2"
                onClick={onClickReinitButton}
              >
                <span>{t('reset')}</span>
                <RevertIcon />
              </RevertButtonStyle>
            )}
        </div>
      </div>
      {(!inputValue ||
        inputTypeSelected === StudyObjectGroupCompareEnum.USERS_LIST) && (
        <div className="flex-none flex flex-col gap-4">
          <div className="flex flex-row gap-4 flex-wrap">
            {inputsTypeConfig
              .filter(
                (inputType) =>
                  inputType.value !== StudyObjectGroupCompareEnum.USERS_LIST,
              )
              .map((inputType) => (
                <InputSelectionButton
                  key={`${keyValue}-${inputType?.value}`}
                  active={inputTypeSelected === inputType?.value}
                  labelKey={inputType.labelKey}
                  icon={inputType.icon}
                  handleOnClickInputSelectionButton={() => {
                    handleSelectValue(null as ValueType)
                    handleOnClickInputSelectionButton(inputType.value)
                  }}
                />
              ))}
          </div>
          <div className="flex">
            {inputsTypeConfig
              .filter(
                (inputType) =>
                  inputType.value === StudyObjectGroupCompareEnum.USERS_LIST,
              )
              .map((inputType) => (
                <InputSelectionButton
                  key={`${keyValue}-${inputType?.value}`}
                  active={inputTypeSelected === inputType?.value}
                  labelKey={inputType.labelKey}
                  icon={inputType.icon}
                  handleOnClickInputSelectionButton={() => {
                    handleSelectValue(null as ValueType)
                    handleOnClickInputSelectionButton(inputType.value)
                  }}
                  isUsersList
                  onFileSelected={(file) => {
                    handleSelectValue(file as ValueType)
                  }}
                  isLarge
                />
              ))}
          </div>
        </div>
      )}
      {isPrivate &&
        filtersActions &&
        handleOnChangeFilter &&
        ((inputTypeSelected as StudyObjectGroupCompareEnum) ===
          StudyObjectGroupCompareEnum.TITLE ||
          (inputTypeSelected as StudyObjectGroupCompareEnum) ===
            StudyObjectGroupCompareEnum.AUTHOR) &&
        inputValue &&
        (inputValue as []).length > 0 && (
          <div>
            <div className="overflow-auto">
              <PerfectScrollbar className="w-full flex-row flex flex-wrap gap-2 justify-center items-center max-h-[80px] pb-3">
                {Object.keys(filtersActions).map((columnKey: string) => (
                  <DynamicsResultsFilterActionTag
                    key={columnKey}
                    columnKey={columnKey}
                    filtersActions={filtersActions}
                    handleRemoveItemFilter={handleRemoveItemFilter}
                    color="#252525"
                  />
                ))}
              </PerfectScrollbar>
            </div>
            <div className="flex flex-row items-center w-full justify-center gap-2">
              {PrivateFiltersItemsAnalyticsConfig.map(
                (item: TrendsItemFilterActionInterface) => (
                  <DynamicsResultsFilterDropDown
                    icon={item.icon}
                    label={t(item.name)}
                    key={item.key}
                    menuKey={item.key}
                    menuOpen={dropdownOpen}
                    handleMenuOpen={setDropdownOpen}
                    whiteTheme
                    isLarge
                  >
                    {groupedBooks[
                      item.key as 'edition' | 'publishing_date' | 'format'
                    ] && (
                      <DynamicsResultsFilterType
                        columnKey={item.key}
                        data={
                          groupedBooks[
                            item.key as 'edition' | 'publishing_date' | 'format'
                          ]
                        }
                        filtersActions={
                          (filtersActions as unknown as {
                            [key: string]:
                              | string
                              | Array<string | number | null>
                              | number
                              | null
                          }) || {}
                        }
                        handleChangeListFilterSelected={(newList) =>
                          onChangeFilters(item.key, newList)
                        }
                        handleYearTypeSelection={handleYearTypeSelection}
                      />
                    )}
                  </DynamicsResultsFilterDropDown>
                ),
              )}
            </div>
          </div>
        )}
      <InputTypeSwitchInput<InputType, ValueType>
        disable={disable}
        color={color}
        type={inputTypeSelected as StudyObjectGroupCompareEnum}
        inputValue={inputValue}
        handleSelectValue={handleSelectValue}
        handleOnClickInputSelectionButton={handleOnClickInputSelectionButton}
        booksSelected={booksSelected}
        handleUpdateBooksSelected={handleUpdateBooksSelected}
        handleOpenBookDetails={handleOpenBookDetails}
        eanSelected={eanSelected}
        filtersActions={filtersActions}
        addInput={addInput}
        removeInput={removeInput}
      />
    </>
  )
}

export default InputTypeChoice
